import { useEffect, useMemo, useState } from "react";
import { IconAlertTriangle } from "@tabler/icons-react";
import {
  Alert,
  BotonExportarExcel,
  Card,
  DesktopOnly,
  Group,
  ListaTarjetasSkeleton,
  MobileOnly,
  PaginadorTabla,
  Stack,
  TablaSkeleton,
  Table,
  Tbody,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { DocumentoAuditEntry } from "../services/auditoria-service";
import { listarAuditoriaDocumentos } from "../services/auditoria-service";
import { useAuditoriaDocumentos } from "../hooks/use-auditoria";
import { categoriasDeDocumentos } from "../utils/filtrar-auditoria";
import { DocumentoAuditMobileCard } from "./DocumentoAuditMobileCard";
import { DocumentoAuditRow } from "./DocumentoAuditRow";
import { DocumentosFiltrosCard } from "./DocumentosFiltrosCard";

const PAGE_SIZE = 15;

/**
 * Sección de auditoría de documentos (cargas + eliminaciones) · decisión 29.
 * TODOS los filtros (acción, categoría, # solicitud con debounce) y el
 * paginado van al SERVIDOR. El nombre del usuario llega resuelto por el backend
 * (hotspot 100 · visible para cualquiera con `auditoria.ver`).
 */
export function SeccionDocumentos() {
  const [accion, setAccion] = useState("");
  const [categoria, setCategoria] = useState("");
  const [numero, setNumero] = useState("");
  const [page, setPage] = useState(1);
  const [numeroDebounced] = useDebouncedValue(numero, 350);

  // Cambiar los filtros invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [accion, categoria, numeroDebounced]);

  const filtrosBase = useMemo(
    () => ({
      ...(accion ? { accion } : {}),
      ...(categoria ? { categoria } : {}),
      ...(numeroDebounced.trim() ? { numeroSolicitud: numeroDebounced.trim() } : {}),
    }),
    [accion, categoria, numeroDebounced],
  );

  const auditoria = useAuditoriaDocumentos({ ...filtrosBase, page, pageSize: PAGE_SIZE });

  const items = auditoria.data?.items ?? [];
  const total = auditoria.data?.total ?? 0;
  const desde = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const hasta = Math.min(page * PAGE_SIZE, total);

  // Opciones del dropdown de categoría · derivadas de la página visible (+ la
  // seleccionada, para que no desaparezca del Select al filtrar).
  const opcionesCategoria = useMemo(
    () =>
      Array.from(
        new Set([...categoriasDeDocumentos(items), ...(categoria ? [categoria] : [])]),
      ).sort(),
    [items, categoria],
  );

  // Export a Excel · trae TODAS las páginas con los filtros actuales.
  const obtenerTodos = async (): Promise<DocumentoAuditEntry[]> => {
    const todos: DocumentoAuditEntry[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarAuditoriaDocumentos({ ...filtrosBase, page: p, pageSize: 100 });
      todos.push(...pagina.items);
      if (todos.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todos;
  };

  if (auditoria.isError) {
    return (
      <Alert variant="error" icon={<IconAlertTriangle size={16} />}>
        Error: {(auditoria.error as Error).message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <DocumentosFiltrosCard
        numero={numero}
        onNumeroChange={setNumero}
        accion={accion}
        onAccionChange={setAccion}
        categoria={categoria}
        onCategoriaChange={setCategoria}
        categoriasDisponibles={opcionesCategoria}
        total={auditoria.data?.total}
        desde={desde}
        hasta={hasta}
      />

      <PaginadorTabla cargando={auditoria.isPending} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="eventos" variante="superior" />

      <DesktopOnly>
        <Card padding="lg">
          <Group justify="flex-end" className="mb-2">
            <BotonExportarExcel
              archivo="auditoria-documentos"
              hoja="Documentos"
              obtenerFilas={obtenerTodos}
              columnas={[
                { header: "Cuándo", value: (e) => e.ocurridoAt },
                { header: "Acción", value: (e) => e.accion },
                { header: "Núm. solicitud", value: (e) => e.numeroSolicitud },
                { header: "Categoría", value: (e) => e.categoria },
                { header: "Archivo", value: (e) => e.nombreOriginal },
                { header: "Tamaño (bytes)", value: (e) => e.tamanoBytes },
                { header: "Usuario", value: (e) => e.usuarioNombre ?? e.usuarioId },
                { header: "Motivo eliminación", value: (e) => e.motivoEliminacion },
              ]}
            />
          </Group>
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm">
              <Thead>
                <Tr>
                  <Th>Cuándo</Th>
                  <Th>Acción</Th>
                  <Th># Solicitud</Th>
                  <Th>Categoría</Th>
                  <Th>Archivo</Th>
                  <Th align="right">Tamaño</Th>
                  <Th>Usuario</Th>
                  <Th>Motivo eliminación</Th>
                </Tr>
              </Thead>
              {auditoria.isPending ? (
                <TablaSkeleton dentroDeTabla columnas={[{}, {}, {}, {}, {}, { align: "right" }, {}, {}]} filas={PAGE_SIZE} />
              ) : (
                <Tbody>
                  {items.map((e) => (
                    <DocumentoAuditRow key={e.id} entry={e} />
                  ))}
                </Tbody>
              )}
            </Table>
          </div>
        </Card>
      </DesktopOnly>

      <MobileOnly>
        {auditoria.isPending ? (
          <ListaTarjetasSkeleton />
        ) : (
          <Stack gap="xs">
            {items.map((e) => (
              <DocumentoAuditMobileCard key={e.id} entry={e} />
            ))}
          </Stack>
        )}
      </MobileOnly>

      <PaginadorTabla cargando={auditoria.isPending} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="eventos" />
    </Stack>
  );
}
